import type { SlideDeck } from '@breakoutlearning/firebase-repository/models/SlideDeck'
import type { SlideDeckAuthor } from '@breakoutlearning/firebase-repository/models/SlideDeckAuthor'
import type { SlideDeckMaterial } from '@breakoutlearning/firebase-repository/models/SlideDeckMaterial'
import type { SlideDeckReference } from '@breakoutlearning/firebase-repository/models/SlideDeckReference'
import {
  SlideDeckMaterialType,
  SlideDeckReferenceType,
} from '@breakoutlearning/firebase-repository/types'
import classNames from 'classnames'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { GoogleStorageImage } from 'components/GoogleStorageImage'
import { StarIcon } from 'components/icons/Star'
import { TriangleRightFillIcon } from 'components/icons/TriangleRightFIll'
import { VideoPreviewDialog } from 'components/VideoPreviewDialog'
import { useDialogs } from 'hooks/dialogs'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo } from 'react'
import {
  selectBannerThumbnail,
  selectSquareThumbnail,
} from './slideDeckCardThumbnail'

function NoThumbnail({ squareThumbnail }: { squareThumbnail?: boolean }) {
  return (
    <div
      className={classNames(
        'flex flex-row items-center justify-center overflow-hidden rounded-xl bg-surface',
        squareThumbnail
          ? 'h-[159px] w-[159px] text-body-small'
          : 'aspect-video max-h-[500px] w-full text-body-large'
      )}
    ></div>
  )
}

export const RevampedSlideDeckTrailerThumbnail = observer(
  function RevampedSlideDeckTrailerThumbnail({
    slideDeckMaterials,
    squareThumbnail = true,
    hideEmptyThumbnail,
    canPlayVideoThumbnails,
  }: {
    slideDeckMaterials: SlideDeckMaterial[]
    squareThumbnail?: boolean
    hideEmptyThumbnail?: boolean
    canPlayVideoThumbnails: boolean
  }) {
    const { showDialog } = useDialogs()
    const { tt } = useTranslationTyped()
    const trailer = slideDeckMaterials.find(
      (material) =>
        material.data.materialType === SlideDeckMaterialType.mp4 &&
        // we need a better way to identify trailers
        material.data.materialName?.toLocaleLowerCase() === 'trailer'
    )

    const onClick = useCallback(
      (
        e:
          | React.KeyboardEvent<HTMLDivElement>
          | React.MouseEvent<HTMLDivElement>
      ) => {
        if (!canPlayVideoThumbnails) return

        const materialLink = trailer?.data.materialLink
        if (!materialLink) return

        e.preventDefault()
        e.stopPropagation()

        showDialog(() => (
          <VideoPreviewDialog
            src={materialLink}
            alt={trailer.data.materialName}
            inline={false}
            requiresAuthHeaders={!trailer.data.viewableByPublic}
          />
        ))
      },
      [trailer, showDialog, canPlayVideoThumbnails]
    )

    // Read inline rather than memoized: the models array reference is stable
    // while the underlying observables change, so a memo keyed on it goes stale.
    const thumbnail = squareThumbnail
      ? selectSquareThumbnail(slideDeckMaterials)
      : selectBannerThumbnail(slideDeckMaterials, trailer)

    if (!slideDeckMaterials?.length) {
      if (hideEmptyThumbnail) return null

      return <NoThumbnail squareThumbnail={squareThumbnail} />
    }

    if (!thumbnail && hideEmptyThumbnail) {
      return null
    }

    const hasTrailer = !!trailer?.data.materialLink

    const canPlayTrailer = hasTrailer && canPlayVideoThumbnails

    const thumbnailKeyboardProps = {
      onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!canPlayVideoThumbnails) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick(e)
        }
      },
      role: canPlayTrailer ? 'button' : undefined,
      tabIndex: canPlayTrailer ? 0 : undefined,
      'aria-label': canPlayTrailer
        ? tt.instructor_library.play_trailer()
        : undefined,
    }

    return (
      <div
        className={classNames(
          'relative select-none overflow-hidden',
          squareThumbnail
            ? 'h-[159px] w-[159px] rounded-xl'
            : 'flex items-center rounded-3xl',
          {
            'max-h-[500px]': !squareThumbnail && canPlayTrailer,
            'max-h-[300px]': !squareThumbnail && !canPlayTrailer,
          }
        )}
        onClick={onClick}
        {...thumbnailKeyboardProps}
      >
        {hasTrailer && canPlayVideoThumbnails && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl">
            <div
              className={classNames(
                'absolute flex cursor-pointer items-center justify-center rounded-full border-[4px] border-fixed-accent-color bg-black bg-opacity-50 p-1',
                {
                  'h-[72.5px] w-[72.5px]': !squareThumbnail,
                  'h-[37px] w-[37px]': squareThumbnail,
                }
              )}
            >
              <TriangleRightFillIcon
                size={50}
                className="fill-white stroke-transparent"
              />
            </div>
          </div>
        )}
        {thumbnail ? (
          <GoogleStorageImage
            aria-hidden
            namespace="slideDeckMaterial"
            src={thumbnail.src}
            alt={thumbnail.alt}
            className={classNames(
              'h-full w-full overflow-hidden object-cover',
              {
                'object-center': !squareThumbnail,
              }
            )}
          />
        ) : (
          <NoThumbnail squareThumbnail={squareThumbnail} />
        )}
      </div>
    )
  }
)

export const RevampedSlideDeckCard = observer(function SlideDeckView({
  slideDeck,
  slideDeckMaterials,
  slideDeckAuthors,
  references,
  onClick,
  href,
}: {
  slideDeck: SlideDeck
  references: SlideDeckReference[]
  slideDeckMaterials: SlideDeckMaterial[]
  slideDeckAuthors?: SlideDeckAuthor[]
  href: string
  onClick: () => void
}) {
  const author = slideDeckAuthors?.[0]
  const authorName = author?.data.authorName || ''
  const { tt } = useTranslationTyped()

  const alternativeTo = references?.[0]

  const referenceDetails = useMemo(() => {
    if (!alternativeTo) return null
    const referenceType = alternativeTo.data.referenceType
    switch (referenceType) {
      case SlideDeckReferenceType.alternativeTo:
        return {
          className: 'border-fixed-accent-color',
          label: tt.instructor_library.alternative_to(),
        }
      case SlideDeckReferenceType.basedOn:
        return {
          className: 'border-on-surface-disabled',
          label: tt.instructor_library.based_on(),
        }
      case SlideDeckReferenceType.supplementTo:
        return {
          className: 'border-on-surface-disabled',
          label: tt.instructor_library.supplement_to(),
        }
      default:
        return {
          className: 'border-fixed-accent-color',
          label: tt.instructor_library.alternative_to(),
        }
    }
  }, [alternativeTo, tt])

  return (
    <a
      tabIndex={0}
      href={href}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick()
      }}
      onClick={(e) => {
        e.preventDefault()
        onClick()
      }}
      data-testid={`slide-deck-card-${slideDeck.id}`}
      className="relative flex cursor-pointer flex-row gap-5 rounded-2.5xl border-3 border-surface-bright bg-surface-bright p-3 hover:border-outline-light"
    >
      {slideDeck.data.slideDeckFeatured && (
        <BreakoutTooltip content={tt.instructor_library.featured()}>
          <span className="absolute left-1 top-1 z-10">
            <StarIcon
              className="h-6 w-6 shrink-0 fill-fixed-accent-color drop-shadow-md md:h-7 md:w-7"
              color="white"
              strokeWidth={1.5}
            />
          </span>
        </BreakoutTooltip>
      )}
      <div className="h-[159px] w-[159px]">
        <RevampedSlideDeckTrailerThumbnail
          slideDeckMaterials={slideDeckMaterials}
          canPlayVideoThumbnails={false}
        />
      </div>
      <div className="grid flex-1 grid-cols-2">
        <div>
          <img
            src={slideDeck.data.slideDeckImageURL}
            className="h-9 p-1"
            alt=""
          />
          <div className="text-title-large text-on-surface">
            {slideDeck.data.slideDeckName}
          </div>
          <div className="text-label-medium text-on-surface">
            {slideDeck.data.slideDeckTeaser}
          </div>
          <div className="text-body-medium text-on-surface">
            {authorName
              ? tt.instructor_library.by_author({ author: authorName })
              : ''}
          </div>
          <div className="mt-2 text-body-medium text-on-surface-var">
            <ul className="list-disc pl-4">
              {slideDeck.data.slideDeckKeyConcepts.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </div>
        {alternativeTo && referenceDetails && (
          <div className="flex flex-col justify-center">
            <div
              className={classNames(
                'min-h-[30%] border-l-2 pl-8',
                referenceDetails.className
              )}
            >
              <div className="mb-2 text-label-medium text-on-surface">
                {referenceDetails.label}
              </div>
              <div className="text-title-small italic text-on-surface">
                {alternativeTo.data.referenceTitle}
              </div>
              <div className="text-body-medium text-on-surface-var">
                {alternativeTo.data.referenceAuthor},{' '}
                {alternativeTo.data.referencePublisher},{' '}
                {alternativeTo.getFormattedPublishedAt()}
              </div>
            </div>
          </div>
        )}
      </div>
    </a>
  )
})
